import { ExternalLink } from 'lucide-react';
import { useState } from 'react';

import { useAbonnements, type AbonnementAdminDto } from '@/shared/api/hooksAdmin';
import { date } from '@/shared/lib/utils';
import { Bouton } from '@/shared/ui/Bouton';
import {
  Carte,
  EnTeteSection,
  Pastille,
  Table,
  type ColonneTable,
  type TonPastille,
} from '@/shared/ui/Table';

const STATUTS = ['Essai', 'Actif', 'EnRetardPaiement', 'Suspendu', 'Annule'] as const;

function ton(statut: string | null | undefined): TonPastille {
  switch (statut) {
    case 'Actif':
      return 'succes';
    case 'Essai':
      return 'accent';
    case 'EnRetardPaiement':
      return 'danger';
    case 'Suspendu':
      return 'alerte';
    default:
      return 'neutre';
  }
}

/**
 * Abonnements SaaS.
 *
 * ECRAN STRICTEMENT CONSULTATIF, et c'est une contrainte, pas une etape : le §8 interdit
 * toute manipulation de fonds depuis cet outil. Il n'existe donc ici aucun bouton de
 * remboursement, de changement de plan ni de relance de prelevement — non pas « pas
 * encore », mais par principe. Ce qui touche a l'argent se fait dans Stripe, qui fait foi,
 * et dont la console a son propre journal.
 *
 * Le lien « Ouvrir dans Stripe » est la seule action : il emmene l'operateur la ou l'acte
 * est legitime, au lieu de reimplementer un pilotage financier a moitie.
 */
export default function AbonnementsPage() {
  const [statut, setStatut] = useState<string | undefined>(undefined);
  const { data, isPending, isError, error, refetch } = useAbonnements(statut);

  const colonnes: readonly ColonneTable<AbonnementAdminDto>[] = [
    {
      cle: 'entreprise',
      entete: 'Entreprise',
      rendu: (ligne) => <span className="font-medium text-texte">{ligne.raisonSociale}</span>,
    },
    { cle: 'plan', entete: 'Plan', rendu: (ligne) => ligne.plan },
    {
      cle: 'statut',
      entete: 'Statut',
      rendu: (ligne) => <Pastille ton={ton(ligne.statut)}>{ligne.statut}</Pastille>,
    },
    {
      cle: 'acces',
      entete: 'Acces',
      rendu: (ligne) =>
        ligne.accesOuvert ? (
          <Pastille ton="succes">Ouvert</Pastille>
        ) : (
          <Pastille ton="danger">Ferme</Pastille>
        ),
    },
    {
      cle: 'essai',
      entete: 'Fin d’essai',
      secondaire: true,
      rendu: (ligne) => <span className="text-texte-doux">{date(ligne.finEssai)}</span>,
    },
    {
      cle: 'periode',
      entete: 'Fin de periode',
      secondaire: true,
      rendu: (ligne) => <span className="text-texte-doux">{date(ligne.finPeriodeCourante)}</span>,
    },
    {
      cle: 'stripe',
      entete: '',
      rendu: (ligne) =>
        ligne.stripeSubscriptionId ? (
          <div className="flex justify-end">
            <Bouton asChild variante="discret" taille="sm">
              <a
                href={`https://dashboard.stripe.com/subscriptions/${ligne.stripeSubscriptionId}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <ExternalLink />
                Stripe
              </a>
            </Bouton>
          </div>
        ) : (
          <span className="block text-right text-texte-faible">—</span>
        ),
    },
  ];

  return (
    <div className="space-y-4">
      <EnTeteSection
        titre="Abonnements"
        description="Etat des abonnements SaaS. Lecture seule."
      />

      <div className="flex flex-wrap gap-1" role="group" aria-label="Filtrer par statut">
        <Bouton
          variante={statut === undefined ? 'secondaire' : 'discret'}
          taille="sm"
          aria-pressed={statut === undefined}
          onClick={() => setStatut(undefined)}
        >
          Tous
        </Bouton>

        {STATUTS.map((valeur) => (
          <Bouton
            key={valeur}
            variante={statut === valeur ? 'secondaire' : 'discret'}
            taille="sm"
            aria-pressed={statut === valeur}
            onClick={() => setStatut(valeur)}
          >
            {valeur}
          </Bouton>
        ))}
      </div>

      <Table
        colonnes={colonnes}
        lignes={data}
        cleLigne={(ligne) => ligne.entrepriseId ?? ''}
        chargement={isPending}
        erreur={isError ? error : undefined}
        onReessayer={() => void refetch()}
        messageVide="Aucun abonnement dans cet etat."
      />

      <Carte className="p-3">
        <p className="text-texte-doux">
          <strong className="text-texte">Aucun mouvement de fonds ne part d’ici.</strong>{' '}
          Remboursements, changements de plan et relances de prelevement se font dans Stripe,
          qui fait foi. Astrolabe n’est qu’un connecteur : il ne detient ni fonds ni donnee
          bancaire.
        </p>
      </Carte>
    </div>
  );
}
